// MARK://dhk8/pos/lib/bridge/canonical-receipt
// Adapters: OrderDto (UI) → CanonicalReceipt (bridge JSON contract).
//
// CONTRATO REAL del bridge — confirmado contra
// /Users/gorlandodb/Projects/nintaigo-bridge/src/NintaiGO.Bridge/EscPos/CanonicalReceipt.cs
// (NO el shape rico business/order/kind que tenía el adapter anterior — ese
// el bridge no lo entiende; deserializaba a TextLine vacíos e imprimía papel
// casi en blanco).
//
// El bridge espera líneas pre-renderizadas para Header/Footer (TextLine con
// align/bold/size). Aquí expandimos la data rica del OrderDto a ese shape:
//   - Header: logo placeholder (text), nombre comercial (bold double), dirección,
//             fiscal block, régimen.
//   - Items: 1 line por item + sub-lines por modifier (negative pricing OK).
//   - Totals: array {Label, AmountCents, Bold}; TOTAL bold + double.
//   - Footer: footerLines como TextLine[].
//   - Qr: URL del ticket público (si publicTicketUuid).
//   - Cut: 'full' por default (consistente con thermal Epson MX).

import type { OrderDto, OrderItem } from '../../stores/order-store.js'
import type { TenantHeaderOpts } from '../print/templates.js'
import type { CanonicalReceipt, TextLine, ReceiptItem, ReceiptTotal } from './bridge-client.js'

interface BuildOpts {
  header: TenantHeaderOpts
  copies?: number
  drawer?: boolean
  /** Host público para QR — default tickets.nintaigo.com */
  publicTicketsHost?: string
}

const WIDTH = 42 // chars por línea a font A en 80mm. Confirmado en EscPosRenderer (default Width).
const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 })
const DATE_FMT = new Intl.DateTimeFormat('es-MX', {
  year: '2-digit', month: '2-digit', day: '2-digit',
  hour: 'numeric', minute: '2-digit', hour12: true,
})

function money(cents: number): string {
  return MXN.format(cents / 100)
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : DATE_FMT.format(d).toLowerCase().replace(/ a\. m\./, ' a.m.').replace(/ p\. m\./, ' p.m.')
}

/** Wrap una línea larga al ancho del papel (WIDTH chars) en múltiples TextLine. */
function wrap(text: string, opts: Omit<TextLine, 'text'> = {}): TextLine[] {
  if (!text) return []
  const lines: string[] = []
  for (const para of text.split('\n')) {
    let buf = ''
    for (const word of para.split(/\s+/)) {
      if ((buf + ' ' + word).trim().length > WIDTH) {
        if (buf) lines.push(buf)
        buf = word
      } else {
        buf = buf ? buf + ' ' + word : word
      }
    }
    if (buf) lines.push(buf)
  }
  return lines.map((t) => ({ text: t, ...opts }))
}

function buildHeader(opts: TenantHeaderOpts): TextLine[] {
  const out: TextLine[] = []
  if (opts.businessName) {
    out.push({ text: opts.businessName, align: 'center', bold: true, size: 'double-h' })
  }
  if (opts.branchName) {
    out.push(...wrap(opts.branchName, { align: 'center' }))
  }
  if (opts.businessAddress) {
    out.push(...wrap(opts.businessAddress, { align: 'center' }))
  }
  if (opts.legalName || opts.rfc) {
    const fiscal = [opts.legalName, opts.rfc ? `RFC ${opts.rfc}` : null].filter(Boolean).join(' · ')
    out.push(...wrap(fiscal, { align: 'center', size: 'normal' }))
  }
  if (opts.regimeFiscal) {
    out.push(...wrap(`Régimen: ${opts.regimeFiscal}`, { align: 'center' }))
  }
  return out
}

/** Lay out "qty × name ............... $amount" en WIDTH chars.
 *  Si el nombre es largo, wrap; el monto siempre va a la derecha en su propia línea
 *  o pegado a la última line del nombre si cabe. Mismo patrón que renderReceipt HTML. */
function itemLine(it: OrderItem): { items: ReceiptItem[]; subLines: TextLine[] } {
  const qty = `${it.quantity} × `
  const amount = money(it.totalCents)
  // Bridge Item shape: {Qty, Name, AmountCents}. El EscPosRenderer.ItemLine layoutea
  // "Qty × Name" left + AmountCents right alineado al WIDTH. No necesitamos format
  // manual; solo pasamos los campos.
  const items: ReceiptItem[] = [{ qty: it.quantity, name: it.name, amountCents: it.totalCents }]

  // Modifiers + notes van como sub-lines (TextLine indented). El bridge no tiene
  // 'sub-items' nativos, así que los emitimos al stream de Lines justo después.
  const subLines: TextLine[] = []
  if (it.modifiers && it.modifiers.length > 0) {
    for (const m of it.modifiers) {
      const delta = m.priceDeltaCents !== 0 ? `  ${m.priceDeltaCents > 0 ? '+' : '-'}${money(Math.abs(m.priceDeltaCents))}` : ''
      subLines.push({ text: `  + ${m.name}${delta}`, align: 'left' })
    }
  }
  if (it.notes) {
    subLines.push({ text: `  "${it.notes}"`, align: 'left' })
  }
  return { items, subLines }
}

function buildItemsBlock(items: OrderItem[]): { items: ReceiptItem[]; lines: TextLine[] } {
  // El bridge renderea Items[] como un bloque continuo (cada item es 1 línea
  // qty/name/amount). Para que los modifiers salgan en sus propias líneas DEBAJO
  // del item, necesitamos mezclar Items con Lines. Como el bridge solo expone
  // Items y Footer/Header como entradas separadas, optamos por:
  //   - Items[] = items principales SIN modifiers
  //   - Lines[] = NO existe en el contract público (Header/Footer sí)
  // Solución pragmática: si hay modifiers, emitimos cada item como TextLine
  // manualmente formateado (qty×name ... amount) usando Header del siguiente
  // bloque o un workaround. Pero el EscPosRenderer SÍ tiene `Lines` (línea 19
  // de CanonicalReceipt.cs es List<TextLine>? Lines), aunque no documentado.
  //
  // Decisión: usar Items[] sin modifiers + agregar las sub-lines de modifiers
  // como Footer leading-lines NO es ideal. Mejor: emitir todo como Lines
  // (TextLine[]) y dejar Items[] vacío. EscPosRenderer.Render itera Items
  // entre Rule/Totals, pero TAMBIÉN itera Lines si están presentes (línea
  // ~246 del .cs). Trade-off: layout manual qty×name...$amount.
  //
  // Path elegido aquí: usar Items[] para los principales (rinde con el helper
  // ItemLine que ya tiene alineado). Para modifiers/notes, los emitimos como
  // Header SUB-block antes del rule (con separadores). NO ideal, pero el más
  // confiable hasta extender el contract con un campo 'subItems' en el .cs.
  const out: ReceiptItem[] = []
  const auxLines: TextLine[] = []
  for (const it of items) {
    const { items: itemsOne, subLines } = itemLine(it)
    out.push(...itemsOne)
    auxLines.push(...subLines)
  }
  return { items: out, lines: auxLines }
}

function buildTotals(order: OrderDto): ReceiptTotal[] {
  const totals: ReceiptTotal[] = []
  totals.push({ label: 'Subtotal', amountCents: order.subtotalCents })
  if (order.discountCents > 0) totals.push({ label: 'Descuento', amountCents: -order.discountCents })
  if (order.taxCents > 0) totals.push({ label: 'IVA 16%', amountCents: order.taxCents })
  if (order.tipCents > 0) totals.push({ label: 'Propina', amountCents: order.tipCents })
  totals.push({ label: 'TOTAL', amountCents: order.totalCents, bold: true })
  return totals
}

function buildMetaLines(order: OrderDto): TextLine[] {
  const lines: TextLine[] = []
  lines.push({ text: ' T I C K E T', align: 'center', bold: true })
  lines.push({ text: '' })
  lines.push({ text: `Folio        ${order.folio}` })
  const when = fmtDate(order.closedAt ?? order.openedAt)
  if (when) lines.push({ text: `Fecha        ${when}` })
  if (order.covers > 0) lines.push({ text: `Comensales   ${order.covers}` })
  return lines
}

function buildFooter(order: OrderDto, host: string, footerLines: string[]): TextLine[] {
  const out: TextLine[] = []
  for (const line of footerLines) {
    out.push(...wrap(line, { align: 'center' }))
  }
  if (order.publicTicketUuid) {
    out.push({ text: '' })
    out.push(...wrap(`${host}/${order.publicTicketUuid}`, { align: 'center' }))
    out.push({ text: 'Consulta tu ticket en línea', align: 'center' })
  }
  return out
}

export function toReceipt(order: OrderDto, opts: BuildOpts): CanonicalReceipt {
  const host = opts.publicTicketsHost ?? 'tickets.nintaigo.com'
  const { items, lines: itemSubLines } = buildItemsBlock(order.items ?? [])
  // Header expandido = business header + meta del ticket + sub-lines de modifiers
  // intercalados después de Items vía Footer no es semántico — el EscPosRenderer
  // separa Header (antes de Rule) y Footer (después de Totals). Para que las
  // sub-lines (modifiers) salgan ENTRE Items y Totals, no hay slot dedicado.
  // Workaround: ponemos sub-lines en Footer antes de los gracias-lines —
  // visualmente quedan al pie. Si el cliente necesita los mods inline con sus
  // items, hay que extender el .cs con un campo `Items[i].subLines` (TODO).
  const header = [
    ...buildHeader(opts.header),
    { text: '' },
    ...buildMetaLines(order),
  ]
  const footerLeading = itemSubLines.length > 0
    ? [{ text: '— Detalle de personalizaciones —', align: 'center' as const }, ...itemSubLines, { text: '' }]
    : []
  const footer = [
    ...footerLeading,
    ...buildFooter(order, host, ['¡Gracias por tu visita!']),
    { text: '' }, { text: '' }, { text: '' }, // feed antes del corte (cortadora rápida)
  ]

  return {
    width: WIDTH,
    encoding: 'cp858',
    header,
    items,
    totals: buildTotals(order),
    footer,
    rule: { char: '-' },
    cut: 'full',
    ...(order.publicTicketUuid ? { qr: { data: `https://${host}/${order.publicTicketUuid}`, size: 6, ecc: 'M' } } : {}),
    ...(opts.drawer ? { cashdrawer: { pin: 2 } } : {}),
  }
}

export function toPrecuenta(order: OrderDto, opts: BuildOpts): CanonicalReceipt {
  const { items, lines: itemSubLines } = buildItemsBlock(order.items ?? [])
  const header = [
    ...buildHeader(opts.header),
    { text: '' },
    { text: 'P R E C U E N T A', align: 'center' as const, bold: true },
    { text: 'NO es comprobante de pago', align: 'center' as const },
    { text: '' },
    { text: `Folio        ${order.folio}` },
    { text: `Fecha        ${fmtDate(order.openedAt)}` },
  ]
  const footer: TextLine[] = []
  if (itemSubLines.length > 0) {
    footer.push({ text: '— Detalle de personalizaciones —', align: 'center' })
    footer.push(...itemSubLines)
    footer.push({ text: '' })
  }
  footer.push({ text: 'Pase a caja para cobrar.', align: 'center' }, { text: '' }, { text: '' }, { text: '' })
  return {
    width: WIDTH,
    encoding: 'cp858',
    header,
    items,
    totals: buildTotals(order),
    footer,
    rule: { char: '-' },
    cut: 'partial',
  }
}

/** Líneas de cocina por platillo: "qty × nombre" con sus modificadores/notas
 *  INDENTADOS debajo (agrupados con el platillo que los pide). Se emiten como
 *  TextLine[] dentro del header — que el bridge SIEMPRE renderiza — para no
 *  depender de soporte de sub-items en el contrato del bridge (BUG prod
 *  2026-06-15: antes los modificadores salían sueltos en el footer y la cocina
 *  no sabía a qué platillo pertenecían). */
function kitchenItemLines(it: OrderItem): TextLine[] {
  const out: TextLine[] = []
  const left = `${it.quantity} × ${it.name}`
  const right = money(it.totalCents)
  if (left.length + 1 + right.length <= WIDTH) {
    out.push({ text: left + ' '.repeat(WIDTH - left.length - right.length) + right, bold: true })
  } else {
    out.push(...wrap(left, { bold: true }))
    out.push({ text: right.padStart(WIDTH) })
  }
  for (const m of it.modifiers ?? []) {
    const delta = m.priceDeltaCents !== 0 ? `  ${m.priceDeltaCents > 0 ? '+' : '-'}${money(Math.abs(m.priceDeltaCents))}` : ''
    out.push({ text: `   + ${m.name}${delta}` })
  }
  if (it.notes) out.push({ text: `   » ${it.notes}` })
  return out
}

export function toKitchen(order: OrderDto, opts: BuildOpts & { stationName?: string; tableLabel?: string; sectionLabel?: string }): CanonicalReceipt {
  // Cocina: sin totales, sin QR, sin fiscal — solo qué cocinar, con cada
  // modificador pegado a su platillo.
  const body: TextLine[] = []
  for (const it of order.items ?? []) body.push(...kitchenItemLines(it))
  // Etiqueta de contexto: para-llevar/domicilio/drive → tipo de servicio; en sala
  // → la SECCIÓN de la mesa (Terraza, VIP…), fallback "EN SALA".
  const typeLabel = order.type === 'TAKEOUT' ? 'PARA LLEVAR'
    : order.type === 'DELIVERY' ? 'A DOMICILIO'
      : order.type === 'DRIVE_THROUGH' ? 'AUTO-SERVICIO'
        : (opts.sectionLabel ? opts.sectionLabel.toUpperCase() : 'EN SALA')
  // Ref de ruteo PROTAGÓNICA (a dónde va el platillo, casa con mostrador/cliente):
  // para-llevar/domicilio/drive → pickupCode (= orderRef); en sala → la MESA
  // (opts.tableLabel). Va GRANDE y enmarcada; el folio queda como traza chica.
  // Sin ninguna (barra sin mesa) → solo folio.
  const hero = order.pickupCode ?? opts.tableLabel ?? null
  const header: TextLine[] = [
    // Margen superior: la cortadora automática deja ~5-6 líneas de zona muerta
    // arriba; sin esto se come el encabezado "COCINA" (bug físico, 2026-06-15).
    { text: '' }, { text: '' }, { text: '' }, { text: '' }, { text: '' }, { text: '' },
    { text: opts.stationName ?? 'COCINA', align: 'center', bold: true, size: 'double-h' },
    { text: typeLabel, align: 'center', bold: true },
  ]
  if (hero) {
    header.push(
      { text: '' },
      { text: '='.repeat(WIDTH) },
      { text: hero, align: 'center', bold: true, size: 'double' },
      { text: '='.repeat(WIDTH) },
      { text: `Folio  ${order.folio}` },
    )
  } else {
    header.push(
      { text: '' },
      { text: `Folio  ${order.folio}` },
    )
  }
  header.push(
    { text: `Hora   ${fmtDate(order.openedAt ?? new Date().toISOString())}` },
    { text: '-'.repeat(WIDTH) },
    ...body,
  )
  return {
    width: WIDTH,
    encoding: 'cp858',
    header,
    items: [],
    // Feed extra antes del corte: la cortadora automática es rápida y se comía
    // la última línea. Estas líneas en blanco dan margen (bridge-agnóstico).
    footer: [{ text: '' }, { text: '' }, { text: '' }, { text: '' }],
    rule: { char: '-' },
    cut: 'full',
  }
}
